<template>
  <d-data-table
    resource="property-logs"
    fluid
    searchable
    :edit-button="false"
    :delete-button="false"
    :filters="true"
    :loading="loading"
    :parameters="parameters"
    :scrollable="true"
  >
    <template #filters>
      <div class="column is-3-widescreen filter is-12-desktop is-12-tablet">
        <div class="card">
          <v-form
            ref="form"
            :data="parameters.filter"
            url="#"
            :method="'post'"
          >
            <div class="">
              <div class="column">
                <b-field :label="$t('click_or_select_date')">
                  <b-datepicker
                    v-model="dateFilter"
                    :size="'is-small'"
                    :locale="$laravel.app.locale"
                    append-to-body
                    :placeholder="$t('click_or_select_date')"
                    range
                    :icon-right="dateFilter.length ? 'close-circle' : ''"
                    icon-right-clickable
                    @icon-right-click="resetFilterRangeDate"
                    @input="setCreatedAtStartEndDate"
                  />
                </b-field>
              </div>
              <div class="column">
                <div class="field mb-1">
                  <label
                    class="label"
                    for="owners"
                  >
                    {{ $t("users").capitalize() }}
                  </label>
                </div>

                <v-autocomplete-fetch
                  :value="user"
                  clearable
                  :route="$laravel.route('api.users.index')"
                  field="name"
                  :placeholder="$t('user').capitalize()"
                  open-on-focus
                  @input="onUserChange($event)"
                />
              </div>
              <div class="column">
                <div class="field mb-1">
                  <label
                    class="label"
                    for="properties"
                  >
                    {{ $t("properties").capitalize() }}
                  </label>
                </div>

                <v-autocomplete-fetch
                  :value="property"
                  clearable
                  :route="$laravel.route('api.properties.index')"
                  field="full_address"
                  :placeholder="$t('properties').capitalize()"
                  open-on-focus
                  @input="onPropertyChange($event)"
                />
              </div>
            </div>
          </v-form>
        </div>
      </div>
    </template>

    <b-table-column
      v-slot="props"
      field="property_id"
      :label="$t('property').capitalize()"
      sortable
    >
      <router-link
        class="has-text-info has-text-weight-semibold is-size-7"
        :to="`/properties/${props.row.property_id}/statuses`"
      >
        {{ props.row.property.full_address_with_unity }}
      </router-link>
    </b-table-column>

    <b-table-column
      v-slot="props"
      field="user_id"
      :label="$t('user').capitalize()"
      sortable
    >
      <span class="is-size-7"> {{ props.row.user.name }} </span>
    </b-table-column>

    <b-table-column
      v-slot="props"
      field="new_status"
      :label="$t('action_type').capitalize()"
      sortable
    >
      <div
        class="tag is-size-7"
      >
        {{ props.row.action_type_label }}
      </div>
    </b-table-column>

    <b-table-column
      v-slot="props"
      field="new_status"
      :label="$t('new_status').capitalize()"
      sortable
    >
      <div
        v-if="props.row.new_status"
        class="tag is-size-7"
      >
        {{ $t(props.row.new_status).capitalize() }}
      </div>
      <span
        v-else
      >-</span>
    </b-table-column>

    <b-table-column
      v-slot="props"
      field="old_status"
      :label="$t('old_status').capitalize()"
      sortable
    >
      <div
        v-if="props.row.old_status"
        class="tag"
      >
        {{ $t(props.row.old_status).capitalize() }}
      </div>
      <span
        v-else
      >-</span>
    </b-table-column>

    <b-table-column
      v-slot="props"
      field="new_price"
      :label="$t('new_price').capitalize()"
      sortable
    >
      <span
        v-if="props.row.new_price"
        class="is-size-7"
      >
        {{ props.row.new_price }}{{ $laravel.currencies.cad.symbol }}
      </span>
      <span
        v-else
      >-</span>
    </b-table-column>

    <b-table-column
      v-slot="props"
      field="old_price"
      :label="$t('old_price').capitalize()"
      sortable
    >
      <span
        v-if="props.row.old_price"
        class="is-size-7"
      >
        {{ props.row.old_price }}{{ $laravel.currencies.cad.symbol }}
      </span>
      <span
        v-else
      >-</span>
    </b-table-column>

    <b-table-column
      v-slot="props"
      field="new_disponibility_date"
      :label="$t('new_disponibility_date').capitalize()"
      sortable
    >
      <span
        v-if="props.row.new_disponibility_date"
        class="is-size-7 no-wrap-text"
      >
        {{ $dayjs(props.row.new_disponibility_date).utc().format("LL") }}
      </span>
      <span
        v-else
      >-</span>
    </b-table-column>

    <b-table-column
      v-slot="props"
      field="old_disponibility_date"
      :label="$t('old_disponibility_date').capitalize()"
      sortable
    >
      <span
        v-if="props.row.old_disponibility_date"
        class="is-size-7 no-wrap-text"
      >
        {{ $dayjs(props.row.old_disponibility_date).utc().format("LL") }}
      </span>
      <span
        v-else
      >-</span>
    </b-table-column>

    <b-table-column
      v-slot="props"
      field="created_at"
      sortable
      :label="$t('attributes.created_at').capitalize()"
    >
      <div class="is-size-7 no-wrap-text">
        {{ $dayjs(props.row.created_at).format("LLL") }}
      </div>
    </b-table-column>
    <b-table-column
      v-slot="props"
      field="deleted_at"
      sortable
      :label="$t('attributes.deleted_at').capitalize()"
    >
      <div
        v-if="props.row.deleted_at"
        class="is-size-7 no-wrap-text"
      >
        {{ $dayjs(props.row.deleted_at).format("LLL") }}
      </div>
      <span
        v-else
      >-</span>
    </b-table-column>

    <b-table-column
      v-slot="props"
      field="restored_at"
      sortable
      :label="$t('attributes.restored_at').capitalize()"
    >
      <div
        v-if="props.row.restored_at"
        class="is-size-7 no-wrap-text"
      >
        {{ $dayjs(props.row.restored_at).format("LLL") }}
      </div>
      <span
        v-else
      >-</span>
    </b-table-column>
  </d-data-table>
</template>

<script>
import AutocompleteFetchVue from "@/plugins/form/components/AutocompleteFetch.vue";

import DataTableFilters from "@/shared/mixins/DataTableFilters";

export default {
  components: {
    "v-autocomplete-fetch": AutocompleteFetchVue,
  },
  mixins: [DataTableFilters],
  data() {
    return {
      filterKey: "property_logs",
      user: null,
      property: null,
      loading: false,
      dateFilter: [],
      parameters: {
        filter: {
          created_at: {
            start_date: null,
            end_date: null,
          },
          user_id: null,
          property_id: null,
        },
      },
    };
  },
  created() {
    const filters = this.getFilters();

    const newParameters = _.cloneDeep(this.parameters);

    filters.forEach(filter => {

      if (filter.key === "start_date") {

        newParameters.filter.created_at.start_date = filter.value;
        this.dateFilter.push(new Date(filter.value));
      }

      if (filter.key === "end_date") {
        newParameters.filter.created_at.end_date = filter.value;
        this.dateFilter.push(new Date(filter.value));
      }

      if (newParameters.filter[filter.key] !== undefined) {

        if (filter.key === "start_date" || filter.key === "end_date") {
          return ;
        }

        if (filter.key === "user_id") {
          this.user = {
            id: filter.value,
            name: filter.label,
          };
        }

        if (filter.key === "property_id") {
          this.property = {
            id: filter.value,
            full_address: filter.label,
          };
        }
        newParameters.filter[filter.key] = filter.value;
      }
    });
    this.parameters = newParameters;
  },
  methods: {
    setCreatedAtStartEndDate(range) {
      var startDate = null;
      var endDate = null;

      if (range && range[0]) {
        startDate = this.$dayjs(range[0]).format("YYYY-MM-DD") ;
      }
      if (range && range[1]) {
        endDate = this.$dayjs(range[1]).format("YYYY-MM-DD");
      }
      this.parameters.filter.created_at = {
        start_date: startDate,
        end_date: endDate,
      };
      this.setFilter("start_date", startDate);
      this.setFilter("end_date", endDate);
    },
    resetFilterRangeDate() {
      this.parameters.filter.created_at = {
        start_date: null,
        end_date: null,
      };
      this.dateFilter = [];

      this.setFilter("start_date", null);
      this.setFilter("end_date", null);
    },
    onUserChange(user) {
      if (user && user.id) {
        this.parameters.filter.user_id = user.id;
        this.setFilter("user_id", user.id, user.name);
      } else {
        this.parameters.filter.user_id = null;
        this.setFilter("user_id", null);
        this.user = null;
      }
    },
    onPropertyChange(property) {
      if (property && property.id) {
        this.parameters.filter.property_id = property.id;

        this.setFilter("property_id", property.id, property.full_address);

      } else {
        this.parameters.filter.property_id = null;
        this.setFilter("property_id", null);
        this.property = null;
      }
    },
  },
};
</script>

<style scoped>
.no-wrap-text {
  text-wrap: nowrap !important;
}
</style>
